import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    fetchValueStreamsSuccess,
    showValueStreamMessage,
    onSaveValueStreamSuccess,
    onDeleteValueStreamSuccess,
    addFavouriteValueStreamSuccess,
    onFetchSelectTeamDataSuccess,
} from 'actions/valueStream';
import {
    ON_SAVE_VALUE_STREAM,
    FETCH_ALL_VALUE_STREAM,
    ON_DELETE_VALUE_STREAM,
    ADD_FAVOURITE_VALUE_STREAM,
    ON_FETCH_SELECT_TEAM_DATA,
    ON_VALUE_STREAM_TEAM_UPDATE,
} from 'constants/ActionTypes';
import { fetchDataRequest, addOrUpdateDataRequest, updateDataRequest, deleteDataRequest  } from 'services/ApiService';
import { apiErrorMessageFormatter } from 'services/errorService';
import { ORGID_PATH_PARAM } from 'constants/api';
import { mapDataToSelectionData } from 'services/selectors';

const currentOrg = (state) => state.orgs.selectedOrg;
const getValueStreamList = (state) => state.valueStreams.allValueStream;
const getSelectedValueStream = (state) => state.valueStreams.selectedValueStream;
const getTeamList = (state) => state.teams.allTeam;
const getSelectedTeams = (state) => state.valueStreams.teamSelectionList;

export function* fetchValueStreams() {
    try {
        const selectedOrg = yield select(currentOrg);
        const fetchedValueStreams = yield call(
            fetchDataRequest,
            'valueStreams',
            [ORGID_PATH_PARAM],
            [selectedOrg ?.id]
        ) || [];
        yield put(fetchValueStreamsSuccess(fetchedValueStreams));
    } catch (error) {
        yield put(showValueStreamMessage(apiErrorMessageFormatter(error)));
    }
}

export function* deleteValueStream({ payload }) {
    try {
        // payload should be the valueStreamId
        const selectedOrg = yield select(currentOrg);
        yield call(
            deleteDataRequest,
            'deleteValueStream',
            'Deleting valueStream '.concat(payload),
            [ORGID_PATH_PARAM, 'valueStreamId'],
            [selectedOrg ?.id, payload]
        ) || [];
        yield put(onDeleteValueStreamSuccess(payload)); // just dispatch the id as the reducer will remove from the item from the list
    } catch (error) {
        yield put(showValueStreamMessage(apiErrorMessageFormatter(error)));
    }
}

export function* saveValueStream({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const allValueStreams = yield select(getValueStreamList);
        let finalPayload = { ...payload };
        // check the orgid of the payload - it will be empty for a new payload - populate from the selectedOrg - otherwise leave it as is
        if (payload ?.orgid === '') {
            finalPayload = { ...payload, orgid: selectedOrg ?.id };
        }
        const savedValueStream = yield call(
            addOrUpdateDataRequest,
            allValueStreams,
            finalPayload,
            'addValueStream',
            'updateValueStream',
            [ORGID_PATH_PARAM],
            [selectedOrg.id]);
        yield put(onSaveValueStreamSuccess(savedValueStream));
    } catch (error) {
        yield put(showValueStreamMessage(apiErrorMessageFormatter(error)));
    }
}

/**
 * This is when the user selects the star on the list page - update the starred attribute of the entity
 * @param {*} param0 
 */
export function* addFavouriteValueStream({ payload }) {
    try {
        const allValueStreams = yield select(getValueStreamList);
        const selectedOrg = yield select(currentOrg);
        const filtered = allValueStreams.filter((entry) => entry ?.id === payload ?.id);

        if (filtered ?.length > 0) {
            const entry = filtered[0];
            const updatedValueStream = {
                ...entry,
                starred: !payload.starred
            };
            const auditMsg = 'Updating starred setting to '.concat(updatedValueStream ?.starred).concat(' for valueStream ').concat(updatedValueStream ?.id);
            yield call(
                updateDataRequest,
                updatedValueStream,
                'updateValueStream',
                auditMsg,
                [ORGID_PATH_PARAM],
                [selectedOrg.id],
            );
            // now just pass on the original payload as the reducer will filter the list as per the state of the selection
            yield put(addFavouriteValueStreamSuccess(payload));
        }
    } catch (error) {
        yield put(showValueStreamMessage(error.messages));
    }
}

export function* onFetchSelectTeamData() {
    const selectedValueStream = yield select(getSelectedValueStream);
    const allTeams = yield select(getTeamList);
    const selectedDrillDownItems = (Array.isArray(selectedValueStream?.teams)) ? selectedValueStream?.teams : [];
    const selections = mapDataToSelectionData(allTeams, selectedDrillDownItems) || null;
    yield put(onFetchSelectTeamDataSuccess(selections));
}

export function* onValueStreamTeamUpdate() {
    try {
        // go through each  - check if entry is being added or removed and cleanup the other object being referenced
        const selectedOrg = yield select(currentOrg);
        const selectedValueStream = yield select(getSelectedValueStream);
        const selectedTeams = yield select(getSelectedTeams);
        const updatedDrillDownSelections = new Array();
        selectedTeams.forEach((selection) => {
            if (selection?.selected) {
                updatedDrillDownSelections.push(selection?.id);
            }
        });
        const updatedSelectedValueStream = {
            ...selectedValueStream,
            teams: updatedDrillDownSelections
        };
        const auditMsg = 'Updating selected team to '.concat(' for valueStream ').concat(updatedSelectedValueStream ?.id);
        yield call(
            updateDataRequest,
            updatedSelectedValueStream,
            'updateValueStream',
            auditMsg,
            [ORGID_PATH_PARAM],
            [selectedOrg.id],
        );
        yield put(onSaveValueStreamSuccess(updatedSelectedValueStream));
    } catch (error) {
        yield put(showValueStreamMessage(apiErrorMessageFormatter(error)));
    }
}

export function* fetchValueStreamsSaga() {
    yield takeEvery(FETCH_ALL_VALUE_STREAM, fetchValueStreams);
}

export function* saveValueStreamSaga() {
    yield takeEvery(ON_SAVE_VALUE_STREAM, saveValueStream);
}

export function* deleteValueStreamSaga() {
    yield takeEvery(ON_DELETE_VALUE_STREAM, deleteValueStream);
}

export function* addFavouriteValueStreamSaga() {
    yield takeEvery(ADD_FAVOURITE_VALUE_STREAM, addFavouriteValueStream);
}

export function* onFetchSelectTeamDataSaga() {
    yield takeEvery(ON_FETCH_SELECT_TEAM_DATA, onFetchSelectTeamData);
}

export function* onValueStreamTeamUpdateSaga() {
    yield takeEvery(ON_VALUE_STREAM_TEAM_UPDATE, onValueStreamTeamUpdate);
}

export default function* rootSaga() {
    yield all([
        fork(fetchValueStreamsSaga),
        fork(saveValueStreamSaga),
        fork(deleteValueStreamSaga),
        fork(addFavouriteValueStreamSaga),
        fork(onFetchSelectTeamDataSaga),
        fork(onValueStreamTeamUpdateSaga),
    ]);
}


// WEBPACK FOOTER //
// ./sagas/valueStream.js